import React, { useEffect, useState } from 'react';
import { useFeathers } from '../../app/util';
import { useSelector, useDispatch } from 'react-redux';
import {
  useParams,
  useHistory,
} from 'react-router-dom';
import { setElections } from '../../app/actions';

import {
  LoadingSpinner,
  DataDetailToolbar,
  ElectionSetupProgress,
  ElectionDatesEditor,
  EditBallotOverview,
  EditBranchCoveragePlan,
  CoveragePlanSummaryCard,
} from '../../components';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const SetupElection = () => {
  const feathers = useFeathers();
  const electionKey = useParams().key;
  const history = useHistory();
  const dispatch = useDispatch();
  
  const electionDataPreloaded = useSelector(state => state?.elections?.byKey[electionKey]);
  const [ election, setElection ] = useState(electionDataPreloaded);
  const [ electionImplementation, setElectionImplementation ] = useState(null);
  const [ precedentElection, setPrecedentElection ] = useState(null);
  const [ loading, setLoading ] = useState(!Boolean(electionDataPreloaded));
  const [ saving, setSaving ] = useState(false);

  const loadElectionData = async () => {
    try {
      const [electionData, eiData] = await Promise.all([
        feathers.getService('elections').get(electionKey),
        feathers.getService('election-implementations').get(electionKey).catch(() => null)
      ]);
      setElection(electionData);
      setElectionImplementation(eiData);

      const precedentKey = electionData?.primaryPrecedent || electionData?.runoffPrecedent;
      if (precedentKey) {
        try {
          const pe = await feathers.getService('elections').get(precedentKey);
          setPrecedentElection(pe);
        } catch (precedentErr) {
          console.error('Failed to load precedent election:', precedentErr);
          setPrecedentElection(null);
        }
      } else {
        setPrecedentElection(null);
      }
      
      // Dispatch to Redux state - get current state and merge
      dispatch((dispatch, getState) => {
        const currentState = getState();
        const currentElections = currentState?.elections || { byKey: {}, list: [], default: null };
        dispatch(setElections({
          ...currentElections,
          byKey: {
            ...currentElections.byKey,
            [electionKey]: electionData
          }
        }));
      });
      
      setLoading(false);
    } catch(err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Calculate current step
  const getCurrentStep = () => {
    if (!election) return 0;
    const setup = election.setup || {};
    const needsPrecedent = (election.runoffPrecedent || election.primaryPrecedent);
    
    // If setup is complete, show completion step
    if (election.setupComplete) {
      return needsPrecedent ? 4 : 3;
    }
    
    if (needsPrecedent) {
      // Order: Dates (0) -> Ballot Overview (1) -> Coverage Plan (2) -> Precedents (3)
      if (!setup.dates) return 0;
      else if (!setup.ballotOverview) return 1;
      else if (!setup.coverage) return 2;
      else if (!setup.precedents) return 3;
      return 3;
    } else {
      if (!setup.dates) return 0;
      else if (!setup.ballotOverview) return 1;
      else if (!setup.coverage) return 2;
      else return 2;
    }
  };

  const currentStep = getCurrentStep();

  useEffect(() => {
    if(feathers && electionKey) {
      loadElectionData();
    }
  }, [feathers, electionKey]);

  // Scroll to top on initial load and when step changes
  useEffect(() => {
    if (!loading) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [loading, currentStep]);

  if(loading) {
    return (
      <Wrapper>
        <LoadingTile>
          <LoadingSpinner scale={0.8}/>
        </LoadingTile>
      </Wrapper>
    );
  }

  const precedentKey = election?.primaryPrecedent || election?.runoffPrecedent;
  const precedentElectionName =
    precedentElection?.name ||
    precedentKey ||
    'the preceding election';

  const markPrecedentsStepComplete = async () => {
    const setupSnapshot = election?.setup || {};
    await feathers.getService('elections').patch(electionKey, {
      setup: {
        ...setupSnapshot,
        precedents: true
      }
    });
  };

  const handleMigrateRacesNow = async () => {
    setSaving(true);
    try {
      await markPrecedentsStepComplete();
      const run = await feathers.getService('election-migration-runs').create({
        targetElectionKey: electionKey
      });
      history.push(`/elections/${electionKey}/election-migration/${run._id}`);
    } catch (err) {
      console.error(err);
      alert('Could not start race migration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleMigrateRacesLater = async () => {
    setSaving(true);
    try {
      await markPrecedentsStepComplete();
      await loadElectionData();
    } catch (err) {
      console.error(err);
      alert('Could not finish setup. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrapper>
      <DataDetailToolbar
        onBack={{ to: {
            pathname: `/elections`,
          }}}
        navTree={[
          {
            text: 'Elections',
            to: {
              pathname: `/elections`,
            }
          },
          { text: 'Setup ' + (election?.name || 'Election') }
        ]}
      />
      <Content>
        <ElectionSetupProgress style={{ marginTop: '24px' }} election={election} />
        {(() => {
          const setup = election?.setup || {};
          const needsPrecedent = (election?.runoffPrecedent || election?.primaryPrecedent);

          // Check if setup is complete
          if (election?.setupComplete) {
            return (
              <CompletionContainer>
                <CheckCircleIcon 
                  sx={{ 
                    fontSize: '120px', 
                    color: '#4caf50',
                    mb: 3
                  }} 
                />
                <Typography variant="h1" sx={{ mb: 3, fontWeight: 600 }}>
                  Nice job!
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => history.push(`/elections/${electionKey}`)}
                  sx={{
                    mt: 2,
                    px: 4,
                    py: 1.5,
                    fontSize: '16px',
                    fontWeight: 600
                  }}
                >
                  Get started researching
                </Button>
              </CompletionContainer>
            );
          }

          // Step 0: Dates
          if (currentStep === 0) {
            return (
              <>
                <ElectionDatesEditor
                  election={election}
                  electionImplementation={electionImplementation}
                  hideBallotContentDisclaimer={true}
                  useStrictValidation={true}
                  saving={saving}
                  onSave={async (changesToElection, changesToEi) => {
                    setSaving(true);
                    try {
                      // Update setup.dates to true
                      const updatedElection = {
                        ...changesToElection,
                        setup: {
                          ...setup,
                          dates: true
                        }
                      };
                      
                      const promises = [
                        feathers.getService('elections').patch(electionKey, updatedElection)
                      ];
                      
                      // Only patch election-implementation if there are changes and it exists
                      if (changesToEi && Object.keys(changesToEi).length > 0) {
                        if (electionImplementation) {
                          promises.push(
                            feathers.getService('election-implementations').patch(electionKey, changesToEi)
                          );
                        } else {
                          // Create election-implementation if it doesn't exist
                          promises.push(
                            feathers.getService('election-implementations').create({
                              key: electionKey,
                              ...changesToEi
                            })
                          );
                        }
                      }
                      
                      await Promise.all(promises);
                      // Reload election data to reflect changes
                      await loadElectionData();
                    } finally {
                      setSaving(false);
                    }
                  }}
                  showSaveButton={true}
                  showCancelButton={false}
                />
              </>
            );
          }

          // Step 1: Ballot Overview
          if (currentStep === 1) {
            return (
              <>
                <div style={{ height: '12px' }} />
                <EditBallotOverview
                  election={election}
                  saving={saving}
                  onSave={async (ballotOverview) => {
                    setSaving(true);
                    try {
                      const updatedElection = {
                        ballotOverview,
                        setup: {
                          ...setup,
                          ballotOverview: true
                        }
                      };
                      await feathers.getService('elections').patch(electionKey, updatedElection);
                      await loadElectionData();
                    } finally {
                      setSaving(false);
                    }
                  }}
                />
              </>
            );
          }

          // Step 2: Coverage Plan
          if (currentStep === 2) {
            return (
              <>
                <div style={{ height: '12px' }} />
                <CoveragePlanSection>
                  <Typography variant="h3">Organization coverage plans</Typography>
                  {/* Organizational coverage plans first */}
                  {electionImplementation?.organizationCoverageAreas?.length > 0 && (electionImplementation?.organizationCoverageAreas || []).map((entry) => (
                    <CoveragePlanSummaryCard
                      key={entry.organizationKey}
                      coverageArea={entry.coverageArea}
                      organizationKey={entry.organizationKey}
                    />
                  ))}
                  {(electionImplementation?.organizationCoverageAreas || [])?.length === 0 && (
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      No organization coverage plans have been added yet.
                    </Typography>
                  )}
                  <BranchPlanBlock>
                    <EditBranchCoveragePlan
                      election={electionKey}
                      onSave={async () => {
                        setSaving(true);
                        try {
                          const updatedElection = {
                            setup: {
                              ...setup,
                              coverage: true
                            }
                          };
                          await feathers.getService('elections').patch(electionKey, updatedElection);
                          await loadElectionData();
                        } finally {
                          setSaving(false);
                        }
                      }}
                    />
                  </BranchPlanBlock>
                </CoveragePlanSection>
              </>
            );
          }

          // Step 3: Precedent / race migration choice (only when needsPrecedent is true)
          if (currentStep === 3 && needsPrecedent) {
            return (
              <PrecedentContainer>
                <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                  Precedent election
                </Typography>
                <Typography variant="body1" color="textPrimary" sx={{ maxWidth: 560, mb: 3 }}>
                  This election will depend on the results of{' '}
                  <strong>{precedentElectionName}</strong>.
                </Typography>
                <Typography variant="body1" color="textPrimary" sx={{ maxWidth: 560, mb: 3 }}>
                  You can copy races from that election now, or finish setup and migrate later from the election menu.
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ alignItems: 'flex-start' }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={saving}
                    onClick={handleMigrateRacesNow}
                    sx={{ fontWeight: 600 }}
                  >
                    Migrate races now
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    disabled={saving}
                    onClick={handleMigrateRacesLater}
                    sx={{ fontWeight: 600 }}
                  >
                    Migrate races later
                  </Button>
                </Stack>
              </PrecedentContainer>
            );
          }

        })()}
      </Content>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  min-height: calc(100% - 24px * 2);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const Content = styled.div`
  width: 100%;
`;

const LoadingTile = styled.div`
  width: 100%;
  align-self: center;
  justify-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CompletionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
`;

const PrecedentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  align-self: flex-start;
  width: 100%;
  text-align: left;
  padding: 48px 40px 48px 32px;
  box-sizing: border-box;
  max-width: 720px;
`;

const CoveragePlanSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 12px;
`;

const BranchPlanBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
  max-width: 600px;
`;

export default SetupElection;
